import {
	Alert,
	Inline,
	Box,
	Container,
	Heading,
	Text,
	Spinner,
	Stack,
	Icons,
	Divider,
	Button,
	Dropdown,
	Component,
	RelativeTime,
	Tabs,
	TabsList,
	TabContent,
	Badge,
	CheckboxInput,
} from "@sembark-travel/ui/base"
import { Breadcrumbs, ButtonLink, NavLink } from "@sembark-travel/ui/router"
import { useXHR } from "@sembark-travel/xhr"
import { Markdown } from "@sembark-travel/ui/markdown"
import useSWR from "swr"
import { generatePath } from "../router-utils"
import { TTravelActivity, TTravelActivityTicketType } from "./store"
import { ImageMediaItem } from "./../Media"
import { Dialog } from "@sembark-travel/ui/dialog"
import {
	arrayMutators,
	CheckboxInputField,
	Form,
	GetFieldValue,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { showSnackbar } from "@sembark-travel/ui/snackbar"

export function TravelActivityDetails({
	travelActivityId,
	children,
}: {
	travelActivityId: number | string
	children?: (props: {
		travelActivity: TTravelActivity
		onChange: () => void
	}) => React.ReactNode
}) {
	const xhr = useXHR()
	const { data, error, mutate } = useSWR<TTravelActivity>(
		`/api/travel-activities/${travelActivityId}`,
		() =>
			xhr
				.get(`/travel-activities/${travelActivityId}`, {
					params: {
						include:
							"latest_media,include_deleted,deleted_ticket_types,trip_destinations,updated_by,created_by,rate_uploaded_by",
					},
				})
				.then((resp) => resp.data.data)
	)
	if (!data && error) return <Alert status="error">{error.message}</Alert>
	if (!data) return <Spinner padding="4" alignCenter />
	const {
		id,
		name,
		ticket_tourist_configurations,
		complimentary_till_age,
		trip_destinations,
		description,
		latest_media,
		deleted_at,
		created_at,
		created_by,
		deleted_by,
		ticket_types,
	} = data
	return (
		<Box key={id}>
			<Breadcrumbs
				title="Travel Activity Details"
				items={[
					[generatePath("/travel-activities"), "Travel Activities"],
					["", name],
				]}
				actions={
					<Inline gap="4">
						{!deleted_at ? (
							<ButtonLink
								to={generatePath("/travel-activities/:travelActivityId/edit", {
									travelActivityId: String(id),
								})}
								query={{ name }}
								size="sm"
							>
								<Icons.Pencil /> Edit
							</ButtonLink>
						) : null}
						<Component<"archive" | "restore" | undefined>
							initialState={undefined}
						>
							{({ state: status, setState: setStatus }) => (
								<>
									<Dropdown alignRight>
										<Dropdown.ToggleButton size="sm" level="tertiary">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											{!deleted_at ? (
												<Dropdown.MenuItem onClick={() => setStatus("archive")}>
													<Icons.Ban /> Disable
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem onClick={() => setStatus("restore")}>
													<Icons.Refresh /> Restore
												</Dropdown.MenuItem>
											)}
										</Dropdown.Menu>
									</Dropdown>
									<Dialog
										open={status === "archive"}
										onClose={() => setStatus(undefined)}
										sm
									>
										<Dialog.Header>
											<Dialog.Title>Disable Travel Activity</Dialog.Title>
										</Dialog.Header>
										<Form<{ travel_activity_id: number }>
											initialValues={{ travel_activity_id: id }}
											onSubmit={withServerErrors(async (values) => {
												const resp = await xhr.delete<{
													data: { message: string }
												}>(`/travel-activities/${values.travel_activity_id}`)
												mutate()
												showSnackbar(resp.data.data.message)
												setStatus(undefined)
											})}
											subscription={{ submitting: true }}
										>
											{({ submitting, handleSubmit }) => (
												<form noValidate onSubmit={handleSubmit}>
													<Dialog.Body>
														<Text>
															This Travel Activity will be removed from all
															travel-activities listing and selection pages.
															Existing usage of this Travel Activity will NOT be
															affected.
														</Text>
														<Divider sm />
														<Text color="warning">
															Are you sure you want to disable this Travel
															Activity ?
														</Text>
													</Dialog.Body>
													<Dialog.Footer>
														<Button
															type="submit"
															disabled={submitting}
															status="warning"
														>
															{submitting
																? "Disabling..."
																: "I understand, disabled it."}
														</Button>
														<Button
															onClick={() => setStatus(undefined)}
															disabled={submitting}
														>
															Cancel
														</Button>
													</Dialog.Footer>
												</form>
											)}
										</Form>
									</Dialog>
									<Dialog
										open={status === "restore"}
										onClose={() => setStatus(undefined)}
										sm
									>
										<Dialog.Header>
											<Dialog.Title>Activity/Ticket Restore</Dialog.Title>
										</Dialog.Header>
										<ActivitiesRestore
											onSuccess={mutate}
											setStatus={setStatus}
											info={
												ticket_types.length
													? "Select the tickets to Restore"
													: "Are you sure you want to restore this Travel Activity ?"
											}
											services={ticket_types}
											ActivitieId={id}
										/>
									</Dialog>
								</>
							)}
						</Component>
					</Inline>
				}
			/>
			<Container paddingY="6" bgColor={"default"}>
				<Inline gap="4" justifyContent="between" collapseBelow="md">
					<Stack gap="4">
						<Stack as="header" gap="1">
							<Heading as="h2">{name}</Heading>
						</Stack>
						<Inline gap="4">
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Trip Destinations
								</Text>
								<Text>
									{trip_destinations?.length
										? trip_destinations.map((t) => t.name).join(", ")
										: "All"}
								</Text>
							</Stack>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Tickets For
								</Text>
								<Text>
									{ticket_tourist_configurations.map((t) => t.name).join(", ")}
								</Text>
							</Stack>
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Complimentary Till
								</Text>
								{complimentary_till_age ? (
									<Text>{complimentary_till_age} yrs</Text>
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Stack>
						</Inline>
						{description ? (
							<Box>
								<Markdown>{description}</Markdown>
							</Box>
						) : null}
						<Inline gap="4" flexWrap="wrap">
							<Stack gap="1">
								<Text color="muted" fontSize="sm">
									Created By
								</Text>
								<Text>{created_by?.name || "You"}</Text>
								<Text fontSize="sm" color="muted">
									<RelativeTime timestamp={created_at} />
								</Text>
							</Stack>
							{deleted_at ? (
								<Stack gap="1">
									<Text color="warning" fontSize="sm">
										Disabled By <Icons.Ban />
									</Text>
									<Text color="warning" fontWeight="semibold">
										{deleted_by?.name || "N/A"}
									</Text>
									<Text fontSize="sm" color="muted">
										<RelativeTime timestamp={deleted_at} />
									</Text>
								</Stack>
							) : null}
						</Inline>
					</Stack>
					<Stack gap="4" style={{ maxWidth: "400px" }}>
						<ImageMediaItem
							media={latest_media}
							help="Image must be 1200x600 JPEG/PNG file with less than 1 MB in size."
							crop={{
								aspect: 2 / 1,
								minHeight: 100,
							}}
							canRemove
							canEdit={!deleted_at}
							onSubmit={async (data) => {
								await xhr.patch(`/travel-activities/${id}/image`, data)
								await mutate()
							}}
						/>
						<Text color="muted" fontSize="sm">
							<Icons.Info /> Fallback image when individual tickets/packages
							image is not available.
						</Text>
					</Stack>
				</Inline>
			</Container>
			<Box borderTopWidth="1">
				<Tabs layout="col">
					<TabsList>
						<NavLink
							to={generatePath("/travel-activities/:travelActivityId", {
								travelActivityId: String(id),
							})}
							end
						>
							Tickets/Packages
						</NavLink>
						<NavLink
							to={generatePath(
								"/travel-activities/:travelActivityId/activities",
								{
									travelActivityId: String(id),
								}
							)}
						>
							Log
						</NavLink>
					</TabsList>
					<TabContent>
						{children
							? children({
									travelActivity: data,
									onChange: mutate,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

function ActivitiesRestore({
	onSuccess,
	setStatus,
	info,
	services,
	ActivitieId,
}: {
	onSuccess: () => void
	ActivitieId: number
	setStatus: (state: undefined) => void
	info: string
	services?: Array<TTravelActivityTicketType & { selected?: boolean }>
}) {
	const xhr = useXHR()
	return (
		<Form<{
			services_list: Array<TTravelActivityTicketType & { selected?: boolean }>
		}>
			initialValues={{ services_list: services }}
			onSubmit={withServerErrors(async (values) => {
				const selectedTicketsIds = values.services_list
					.filter((s) => s.selected)
					.map((s) => s.id)
				const url = `/travel-activities/${ActivitieId}/restore`
				const resp = await xhr.patch<{
					data: { message: string }
				}>(url, {
					ticket_type_ids: selectedTicketsIds,
				})
				onSuccess()
				showSnackbar(resp.data.data.message)
				setStatus(undefined)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Dialog.Body>
						<Stack gap={"6"}>
							<Text>{info}</Text>
							{services?.length ? (
								<Stack gap="4">
									<Inline justifyContent={"start"}>
										<GetFieldValue<
											Array<TTravelActivityTicketType & { selected?: boolean }>
										>
											name={"services_list"}
										>
											{({
												value: ticketsFieldValue,
												onChange: changeTicketFieldValue,
											}) => {
												const selectedTickets = ticketsFieldValue.filter(
													(s) => s.selected
												)
												return (
													<Box
														as="label"
														padding="2"
														display="flex"
														alignItems="center"
														bgColor={{ default: "subtle", hover: "inset" }}
														rounded="lg"
														cursor="pointer"
													>
														<CheckboxInput
															checked={
																services.length === selectedTickets.length
															}
															onChange={(e) => {
																if (e.currentTarget.checked) {
																	changeTicketFieldValue(
																		ticketsFieldValue.map((t) => ({
																			...t,
																			selected: true,
																		}))
																	)
																} else {
																	changeTicketFieldValue(
																		ticketsFieldValue.map((t) => ({
																			...t,
																			selected: false,
																		}))
																	)
																}
															}}
														/>
														<Box marginLeft="2" fontWeight="semibold">
															Select All Tickets
														</Box>
													</Box>
												)
											}}
										</GetFieldValue>
									</Inline>
									<Stack gap={"1"} flex="1">
										{(services || []).map((s, serviceIndex) => {
											const checkboxInputFieldName = `services_list[${serviceIndex}].selected`
											return (
												<Box key={serviceIndex}>
													<GetFieldValue name={checkboxInputFieldName}>
														{({ value: selected }) => (
															<Inline
																key={checkboxInputFieldName}
																as="label"
																paddingY="2"
																paddingX="4"
																rounded="lg"
																backgroundColor={{
																	default: selected ? "primary" : "subtle",
																	hover: "primary",
																}}
															>
																<Stack flex="1" minWidth="0" gap="px">
																	<Text>{s.display_name}</Text>
																	{s.short_name ? (
																		<Text fontWeight="normal" color={"muted"}>
																			{s.short_name}
																		</Text>
																	) : null}
																</Stack>
																<Box>
																	<CheckboxInputField
																		name={checkboxInputFieldName}
																		type="checkbox"
																	/>
																</Box>
															</Inline>
														)}
													</GetFieldValue>
												</Box>
											)
										})}
									</Stack>
									<GetFieldValue<
										Array<TTravelActivityTicketType & { selected?: boolean }>
									> name="services_list">
										{({ value: services_list = [] }) => {
											const selectedServices = services_list.filter(
												(s) => s.selected
											)
											return (
												<Box>
													<Text fontWeight="semibold">
														Selected Tickets ({selectedServices.length})
													</Text>

													<Inline
														gap={"2"}
														borderWidth="1"
														rounded="md"
														padding={"4"}
														justifyContent="between"
														alignItems={"center"}
														borderLeftWidth={"4"}
													>
														<Inline flexWrap={"wrap"} gap="2">
															{selectedServices.map((s) => (
																<Inline key={s.id}>
																	<Box
																		as="span"
																		display="inline"
																		paddingX="1"
																		opacity="70"
																	>
																		•
																	</Box>
																	<Box>
																		<Text>{s.display_name || s.name}</Text>
																		{s.short_name ? (
																			<Text fontWeight="normal" color={"muted"}>
																				{s.short_name}
																			</Text>
																		) : null}
																	</Box>
																</Inline>
															))}
														</Inline>
														<Box>
															<Badge success>selected</Badge>
														</Box>
													</Inline>
												</Box>
											)
										}}
									</GetFieldValue>
								</Stack>
							) : null}
						</Stack>
					</Dialog.Body>
					<Dialog.Footer>
						<Button type="submit" disabled={submitting}>
							{submitting ? "Restoring..." : "Restore"}
						</Button>
						<Button onClick={() => setStatus(undefined)} disabled={submitting}>
							Cancel
						</Button>
					</Dialog.Footer>
				</form>
			)}
		</Form>
	)
}
